import { useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Ic } from './shared';
import * as api from '../lib/api';

/* ═══════════════════════════════════════════════════════════════
   ENHANCED DEBT ENTRY: 4-TIER FLOW
   1. Plaid Bank Connection (auto-import)
   2. Statement Upload (AI extraction via camera/file)
   3. Manual Entry (dynamic forms with 11 debt types)
   4. Edit/Review before saving
   ═══════════════════════════════════════════════════════════════ */

// ── Additional Icons ─────────────────────────────────────────────
const IcEx = {
  hospital: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><path d="M12 2v20M17 7h-5V2H12v5H7" /><rect x="5" y="7" width="14" height="14" rx="2" /></svg>,
  collection: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>,
  tax: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /><path d="M7 15h.01M11 15h2" /></svg>,
  home: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><path d="M3 10.2L12 3l9 7.2V20a2 2 0 01-2 2H5a2 2 0 01-2-2z" /></svg>,
  graduation: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10l-10-5-10 5 10 5z" /><path d="M6 12v5a2 2 0 002 2h8a2 2 0 002-2v-5" /></svg>,
  bag: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>,
  truck: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-3" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
  cash: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M6 12h.01M18 12h.01" /></svg>,
  piggy: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h6v2h4v-4c1-.5 1.5-1 1.5-2.5V11c2.3-.5 4-2.4 4-5h-2c0 2.8-2.2 5-5 5z" /><circle cx="2" cy="9" r="1" /></svg>,
  mca: (s = 18, c = MUTE) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /><circle cx="15" cy="15" r="2" /></svg>,
};

// ── Empty Form State ──────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', type: 'credit', debt_subtype: '', balance: '', original: '', apr: '',
  min_payment: '', due: '', mask: '', statement_name: '', credit_limit: '', loan_term: '', payment_frequency: 'monthly',
  installments_remaining: '', next_payment_date: '', late_fee: '', cash_price: '',
  total_lease_cost: '', early_buyout: '', lease_end_date: '', loan_servicer: '',
  loan_type: '', provider: '', item_description: '', factor_rate: '', holdback_percentage: '',
  daily_sales: '', total_amount_owed: '', fee_amount: '', due_date: '', original_creditor: '',
  collection_agency: '', tax_years: '', installment_agreement: '', penalties_accruing: false,
  escrow_included: false, payoff_date: ''
};

// ── Debt Type Definitions ─────────────────────────────────────────
const DEBT_TYPES = {
  // COMMON
  credit: { id: 'credit', label: 'Credit Card', icon: Ic.credit, group: 'common' },
  auto: { id: 'auto', label: 'Auto Loan', icon: Ic.car, group: 'common' },
  personal: { id: 'personal', label: 'Personal Loan', icon: Ic.loan, group: 'common' },
  student: { id: 'student', label: 'Student Loan', icon: IcEx.graduation, group: 'common' },
  mortgage: { id: 'mortgage', label: 'Mortgage / Home Equity', icon: IcEx.home, group: 'common' },
  
  // ALTERNATIVE
  bnpl: { id: 'bnpl', label: 'BNPL', icon: IcEx.bag, group: 'alternative' },
  lease: { id: 'lease', label: 'Lease-to-Own', icon: IcEx.truck, group: 'alternative' },
  payday: { id: 'payday', label: 'Payday Loan', icon: IcEx.cash, group: 'alternative' },
  title: { id: 'title', label: 'Title Loan', icon: Ic.car, group: 'alternative' },
  mca: { id: 'mca', label: 'MCA', icon: IcEx.mca, group: 'alternative' },
  
  // OTHER
  medical: { id: 'medical', label: 'Medical Debt', icon: IcEx.hospital, group: 'other' },
  collections: { id: 'collections', label: 'Collections', icon: IcEx.collection, group: 'other' },
  tax: { id: 'tax', label: 'IRS / Tax Debt', icon: IcEx.tax, group: 'other' },
  loan401k: { id: 'loan401k', label: '401k Loan', icon: IcEx.piggy, group: 'other' },
};

// Provider Options
const BNPL_PROVIDERS = ['Affirm', 'Klarna', 'Afterpay', 'Sezzle', 'Zip', 'Other'];
const LEASE_PROVIDERS = ['Rent-A-Center', "Aaron's", 'Progressive Leasing', 'Acima', 'Other'];
const PAYMENT_FREQUENCIES = ['weekly', 'biweekly', 'monthly'];

// ── Field Configurations ──────────────────────────────────────────
const FIELD_CONFIGS = {
  credit: [
    { key: 'name', label: 'CARD NAME', ph: 'e.g. Chase Sapphire', type: 'text', req: true },
    { key: 'mask', label: 'LAST 4 DIGITS', ph: 'e.g. 5154', type: 'text', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. CITI AUTOPAY or CHASE CRCARDPMT', type: 'text' },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'credit_limit', label: 'CREDIT LIMIT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'APR %', ph: '0', type: 'number', req: true, validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'MINIMUM PAYMENT', ph: '$0', type: 'currency', req: true },
  ],
  
  auto: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. Toyota Financial', type: 'text', req: true },
    { key: 'mask', label: 'LAST 4 OF ACCOUNT #', ph: 'e.g. 7890', type: 'text', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. TOYOTA FINANCIAL PMT', type: 'text' },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'original', label: 'ORIGINAL LOAN AMOUNT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'APR %', ph: '0', type: 'number', req: true, validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT', ph: '$0', type: 'currency', req: true },
    { key: 'loan_term', label: 'REMAINING TERM (MONTHS)', ph: '36', type: 'number' },
  ],
  
  personal: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. SoFi, LendingClub', type: 'text', req: true },
    { key: 'mask', label: 'LAST 4 OF ACCOUNT #', ph: 'e.g. 1234', type: 'text', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. SOFI LOAN PMT', type: 'text' },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'original', label: 'ORIGINAL LOAN AMOUNT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'APR %', ph: '0', type: 'number', req: true, validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT', ph: '$0', type: 'currency', req: true },
    { key: 'loan_term', label: 'REMAINING TERM (MONTHS)', ph: '36', type: 'number' },
  ],
  
  student: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. Federal Direct Loan', type: 'text', req: true },
    { key: 'mask', label: 'LAST 4 OF ACCOUNT #', ph: 'e.g. 5678', type: 'text', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. NAVIENT or MOHELA PMT', type: 'text' },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'original', label: 'ORIGINAL LOAN AMOUNT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'APR %', ph: '0', type: 'number', req: true, validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT', ph: '$0', type: 'currency', req: true },
    { key: 'loan_term', label: 'REMAINING TERM (MONTHS)', ph: '120', type: 'number' },
    { key: 'loan_servicer', label: 'SERVICER', ph: 'e.g. Nelnet, MOHELA', type: 'text' },
    { key: 'loan_type', label: 'LOAN TYPE', type: 'select', opts: ['Federal', 'Private'] },
  ],
  
  mortgage: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. Wells Fargo', type: 'text', req: true },
    { key: 'mask', label: 'LAST 4 OF ACCOUNT #', ph: 'e.g. 9012', type: 'text', maxLength: 4 },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. WELLS FARGO MORTGAGE', type: 'text' },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'original', label: 'ORIGINAL LOAN AMOUNT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'APR %', ph: '0', type: 'number', req: true, validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT', ph: '$0', type: 'currency', req: true },
    { key: 'loan_term', label: 'REMAINING TERM (MONTHS)', ph: '360', type: 'number' },
    { key: 'escrow_included', label: 'ESCROW INCLUDED?', type: 'toggle' },
  ],
  
  bnpl: [
    { key: 'provider', label: 'PROVIDER', type: 'select', opts: BNPL_PROVIDERS, req: true },
    { key: 'item_description', label: 'ITEM / PURCHASE DESCRIPTION', ph: 'e.g. Nike Shoes', type: 'text', req: true },
    { key: 'statement_name', label: 'NAME ON BANK STATEMENT', ph: 'e.g. KLARNA or AFFIRM PMT', type: 'text' },
    { key: 'balance', label: 'REMAINING BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'min_payment', label: 'PAYMENT AMOUNT PER INSTALLMENT', ph: '$0', type: 'currency', req: true },
    { key: 'installments_remaining', label: 'PAYMENTS REMAINING', ph: '3', type: 'number', req: true, validate: (v) => v > 0 },
    { key: 'apr', label: 'APR (IF ANY)', ph: '0', type: 'number', validate: (v) => v >= 0 && v <= 100 },
  ],
  
  lease: [
    { key: 'provider', label: 'PROVIDER', type: 'select', opts: LEASE_PROVIDERS, req: true },
    { key: 'item_description', label: 'ITEM DESCRIPTION', ph: 'e.g. Laptop', type: 'text', req: true },
    { key: 'balance', label: 'REMAINING BALANCE / BUYOUT AMOUNT', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'min_payment', label: 'WEEKLY OR MONTHLY PAYMENT', ph: '$0', type: 'currency', req: true },
    { key: 'payment_frequency', label: 'PAYMENT FREQUENCY', type: 'select', opts: PAYMENT_FREQUENCIES, req: true },
    { key: 'installments_remaining', label: 'PAYMENTS REMAINING', ph: '12', type: 'number', req: true, validate: (v) => v > 0 },
  ],
  
  payday: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. Check Into Cash', type: 'text', req: true },
    { key: 'original', label: 'AMOUNT BORROWED', ph: '$0', type: 'currency', req: true },
    { key: 'total_amount_owed', label: 'TOTAL AMOUNT OWED', ph: '$0', type: 'currency', req: true },
    { key: 'due_date', label: 'DUE DATE', ph: 'MM/DD/YYYY', type: 'date', req: true },
    { key: 'fee_amount', label: 'FEE AMOUNT', ph: '$0', type: 'currency' },
  ],
  
  title: [
    { key: 'name', label: 'LENDER NAME', ph: 'e.g. TitleMax', type: 'text', req: true },
    { key: 'original', label: 'AMOUNT BORROWED', ph: '$0', type: 'currency', req: true },
    { key: 'total_amount_owed', label: 'TOTAL AMOUNT OWED', ph: '$0', type: 'currency', req: true },
    { key: 'due_date', label: 'DUE DATE', ph: 'MM/DD/YYYY', type: 'date', req: true },
    { key: 'fee_amount', label: 'FEE AMOUNT', ph: '$0', type: 'currency' },
  ],
  
  mca: [
    { key: 'name', label: 'FUNDER NAME', ph: 'e.g. OnDeck', type: 'text', req: true },
    { key: 'original', label: 'ORIGINAL ADVANCE AMOUNT', ph: '$0', type: 'currency', req: true },
    { key: 'balance', label: 'CURRENT BALANCE / REMAINING PAYOFF', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'factor_rate', label: 'FACTOR RATE', ph: '1.35', type: 'number', req: true, validate: (v) => v >= 1.0 && v <= 2.0 },
    { key: 'holdback_percentage', label: 'HOLDBACK PERCENTAGE', ph: '15', type: 'number', req: true, validate: (v) => v >= 1 && v <= 100 },
    { key: 'daily_sales', label: 'ESTIMATED DAILY SALES VOLUME', ph: '$0', type: 'currency' },
  ],
  
  medical: [
    { key: 'provider', label: 'PROVIDER / HOSPITAL NAME', ph: 'e.g. St. Mary Hospital', type: 'text', req: true },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT (IF ON PLAN)', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'INTEREST RATE (IF ANY)', ph: '0', type: 'number', validate: (v) => v >= 0 && v <= 100 },
  ],
  
  collections: [
    { key: 'original_creditor', label: 'ORIGINAL CREDITOR', ph: 'e.g. Capital One', type: 'text', req: true },
    { key: 'collection_agency', label: 'COLLECTION AGENCY', ph: 'e.g. Portfolio Recovery', type: 'text', req: true },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'min_payment', label: 'MONTHLY PAYMENT (IF ON PLAN)', ph: '$0', type: 'currency' },
  ],
  
  tax: [
    { key: 'tax_years', label: 'TAX YEAR(S)', ph: '2023, 2024', type: 'text', req: true },
    { key: 'balance', label: 'TOTAL OWED', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'installment_agreement', label: 'MONTHLY INSTALLMENT AGREEMENT AMOUNT', ph: '$0', type: 'currency' },
    { key: 'penalties_accruing', label: 'INTEREST + PENALTIES ACCRUING?', type: 'toggle' },
  ],
  
  loan401k: [
    { key: 'name', label: 'LOAN NAME', ph: 'e.g. 401k Loan', type: 'text', req: true },
    { key: 'balance', label: 'CURRENT BALANCE', ph: '$0', type: 'currency', req: true, validate: (v) => v > 0 },
    { key: 'original', label: 'ORIGINAL LOAN AMOUNT', ph: '$0', type: 'currency' },
    { key: 'apr', label: 'INTEREST RATE', ph: '0', type: 'number', validate: (v) => v >= 0 && v <= 100 },
    { key: 'min_payment', label: 'PAYMENT AMOUNT (PER PAYCHECK)', ph: '$0', type: 'currency', req: true },
    { key: 'loan_term', label: 'REMAINING TERM (MONTHS)', ph: '48', type: 'number' },
  ],
};

// ── Styles ────────────────────────────────────────────────────────
const INPUT_STYLE = {
  width: '100%', padding: '12px 14px', background: 'var(--faint)', border: '1px solid var(--ghost)',
  color: 'var(--light)', fontFamily: BODY, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  borderRadius: 8, marginBottom: 6,
};

const TYPE_CARD_STYLE = (isActive) => ({
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 8, padding: '16px 12px', background: isActive ? 'rgba(232,168,73,0.12)' : 'var(--faint)',
  border: isActive ? '1.5px solid rgba(232,168,73,0.5)' : '1px solid var(--ghost)',
  borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s ease',
  minWidth: 90, textAlign: 'center',
});

const TOGGLE_STYLE = (on) => ({
  width: 44, height: 24, borderRadius: 12, background: on ? GOLD : 'var(--ghost)',
  border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
});

const TOGGLE_DOT = (on) => ({
  position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: '50%',
  background: on ? BASE : 'var(--mute)', transition: 'left 0.2s',
});

/* ═══════════════════════════════════════════════════════════════
   SUB-COMPONENTS
   ═══════════════════════════════════════════════════════════════ */

// Type Selector Grid
function TypeSelectorGrid({ selectedType, onSelect }) {
  const groups = {
    common: Object.values(DEBT_TYPES).filter(t => t.group === 'common'),
    alternative: Object.values(DEBT_TYPES).filter(t => t.group === 'alternative'),
    other: Object.values(DEBT_TYPES).filter(t => t.group === 'other'),
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 12 }}>SELECT DEBT TYPE</Mo>
      
      {/* Common */}
      <Mo style={{ fontSize: 9, color: GOLD, letterSpacing: 1.5, marginBottom: 8 }}>COMMON</Mo>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8, marginBottom: 20 }}>
        {groups.common.map(type => (
          <div
            key={type.id}
            onClick={() => onSelect(type.id)}
            style={TYPE_CARD_STYLE(selectedType === type.id)}
            data-testid={`type-${type.id}`}
          >
            {type.icon(22, selectedType === type.id ? GOLD : MUTE)}
            <Mo style={{ fontSize: 9, color: selectedType === type.id ? GOLD : 'var(--mute)', fontWeight: 600 }}>
              {type.label.toUpperCase()}
            </Mo>
          </div>
        ))}
      </div>
      
      {/* Alternative */}
      <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 8 }}>ALTERNATIVE</Mo>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8, marginBottom: 20 }}>
        {groups.alternative.map(type => (
          <div
            key={type.id}
            onClick={() => onSelect(type.id)}
            style={TYPE_CARD_STYLE(selectedType === type.id)}
            data-testid={`type-${type.id}`}
          >
            {type.icon(22, selectedType === type.id ? GOLD : MUTE)}
            <Mo style={{ fontSize: 9, color: selectedType === type.id ? GOLD : 'var(--mute)', fontWeight: 600 }}>
              {type.label.toUpperCase()}
            </Mo>
          </div>
        ))}
      </div>
      
      {/* Other */}
      <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 8 }}>OTHER</Mo>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
        {groups.other.map(type => (
          <div
            key={type.id}
            onClick={() => onSelect(type.id)}
            style={TYPE_CARD_STYLE(selectedType === type.id)}
            data-testid={`type-${type.id}`}
          >
            {type.icon(22, selectedType === type.id ? GOLD : MUTE)}
            <Mo style={{ fontSize: 9, color: selectedType === type.id ? GOLD : 'var(--mute)', fontWeight: 600 }}>
              {type.label.toUpperCase()}
            </Mo>
          </div>
        ))}
      </div>
    </div>
  );
}

// Field Renderer
function DynamicField({ field, value, onChange, errors }) {
  const hasError = errors[field.key];
  
  if (field.type === 'toggle') {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5 }}>{field.label}</Mo>
          <button
            onClick={() => onChange(field.key, !value)}
            style={TOGGLE_STYLE(value)}
            data-testid={`toggle-${field.key}`}
          >
            <div style={TOGGLE_DOT(value)} />
          </button>
        </div>
      </div>
    );
  }
  
  if (field.type === 'select') {
    return (
      <div style={{ marginBottom: 16 }}>
        <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 6 }}>
          {field.label}{field.req && <span style={{ color: EMBER }}>*</span>}
        </Mo>
        <select
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          style={{ ...INPUT_STYLE, cursor: 'pointer', border: hasError ? `1px solid ${EMBER}` : '1px solid var(--ghost)' }}
          data-testid={`select-${field.key}`}
        >
          <option value="">Select...</option>
          {field.opts.map(opt => <option key={opt} value={opt.toLowerCase()}>{opt}</option>)}
        </select>
        {hasError && <Mo style={{ fontSize: 9, color: EMBER, marginTop: 4 }}>{errors[field.key]}</Mo>}
      </div>
    );
  }
  
  if (field.type === 'date') {
    return (
      <div style={{ marginBottom: 16 }}>
        <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 6 }}>
          {field.label}{field.req && <span style={{ color: EMBER }}>*</span>}
        </Mo>
        <input
          type="date"
          value={value || ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          style={{ ...INPUT_STYLE, border: hasError ? `1px solid ${EMBER}` : '1px solid var(--ghost)' }}
          data-testid={`date-${field.key}`}
        />
        {hasError && <Mo style={{ fontSize: 9, color: EMBER, marginTop: 4 }}>{errors[field.key]}</Mo>}
      </div>
    );
  }
  
  // Default: text, number, currency
  return (
    <div style={{ marginBottom: 16 }}>
      <Mo style={{ fontSize: 10, color: 'var(--mute)', letterSpacing: 1.5, marginBottom: 6 }}>
        {field.label}{field.req && <span style={{ color: EMBER }}>*</span>}
      </Mo>
      <input
        type={field.type === 'currency' || field.type === 'number' ? 'number' : 'text'}
        placeholder={field.ph}
        value={value || ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        step={field.type === 'currency' ? '0.01' : field.type === 'number' && field.key === 'factor_rate' ? '0.01' : '1'}
        min={field.type === 'currency' || field.type === 'number' ? '0' : undefined}
        maxLength={field.maxLength || undefined}
        style={{ ...INPUT_STYLE, border: hasError ? `1px solid ${EMBER}` : '1px solid var(--ghost)' }}
        data-testid={`input-${field.key}`}
      />
      {field.key === 'statement_name' && (
        <Mo style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4, lineHeight: 1.4 }}>
          Check your bank app — look for how this payment appears in your checking account. This helps us match payments automatically.
        </Mo>
      )}
      {hasError && <Mo style={{ fontSize: 9, color: EMBER, marginTop: 4 }}>{errors[field.key]}</Mo>}
    </div>
  );
}

// Helper Tooltip for MCA
function McaTooltip() {
  return (
    <div style={{
      padding: 12, background: 'rgba(232,168,73,0.08)', border: '1px solid rgba(232,168,73,0.2)',
      borderRadius: 8, marginBottom: 16
    }}>
      <Mo style={{ fontSize: 10, color: GOLD, fontWeight: 600, marginBottom: 4 }}>💡 ABOUT MCAs</Mo>
      <Bd style={{ fontSize: 11, color: 'var(--mute)', lineHeight: 1.5 }}>
        MCAs aren't traditional loans. The funder takes a percentage of your daily sales until the advance is repaid.
      </Bd>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

export default function DebtEntryScreen({ onComplete, onCancel, navigate, refreshData }) {
  const [entryMethod, setEntryMethod] = useState(null); // null = show options, 'plaid', 'upload', 'manual'
  const [uploading, setUploading] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidSyncing, setPlaidSyncing] = useState(false);
  
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Plaid integration - fetch link token when user selects Plaid
  useEffect(() => {
    if (entryMethod === 'plaid' && !linkToken) {
      fetchPlaidLinkToken();
    }
  }, [entryMethod]);

  const fetchPlaidLinkToken = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/create_link_token`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      setLinkToken(data.link_token);
    } catch (err) {
      console.error('Failed to get Plaid link token:', err);
      alert('Failed to connect to Plaid. Please try manual entry.');
      setEntryMethod(null);
    }
  };

  const handlePlaidSuccess = async (public_token, metadata) => {
    setPlaidSyncing(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plaid/exchange_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token, metadata }),
        credentials: 'include',
      });
      
      // Success - complete and go back
      if (onComplete) onComplete();
      else if (refreshData && navigate) {
        await refreshData();
        navigate(null, 'home');
      }
    } catch (err) {
      console.error('Plaid sync failed:', err);
      alert('Bank connection failed. Please try again.');
      setPlaidSyncing(false);
      setEntryMethod(null);
    }
  };

  const { open: openPlaidLink, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
  });

  // Auto-open Plaid when ready
  useEffect(() => {
    if (entryMethod === 'plaid' && plaidReady && linkToken) {
      openPlaidLink();
    }
  }, [entryMethod, plaidReady, linkToken, openPlaidLink]);

  // Statement upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statement/parse`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!res.ok) throw new Error('Upload failed');
      
      const data = await res.json();
      if (data.success && data.extracted) {
        const e = data.extracted;
        // Pre-fill form with ALL extracted data
        setForm({
          ...EMPTY_FORM,
          name:                    e.name || '',
          type:                    e.type || 'credit',
          debt_subtype:            e.type || '',
          balance:                 e.balance > 0 ? String(e.balance) : '',
          original:                e.original > 0 ? String(e.original) : '',
          apr:                     e.apr > 0 ? String(e.apr) : '',
          min_payment:             e.min_payment > 0 ? String(e.min_payment) : '',
          due:                     e.due || '',
          credit_limit:            e.credit_limit > 0 ? String(e.credit_limit) : '',
          cash_price:              e.cash_price > 0 ? String(e.cash_price) : '',
          total_lease_cost:        e.total_lease_cost > 0 ? String(e.total_lease_cost) : '',
          early_buyout:            e.early_buyout > 0 ? String(e.early_buyout) : '',
          installments_remaining:  e.installments_remaining > 0 ? String(e.installments_remaining) : '',
          payment_frequency:       e.payment_frequency || 'monthly',
          next_payment_date:       e.next_payment_date || '',
          loan_servicer:           e.loan_servicer || '',
          provider:                e.provider || '',
          loan_type:               e.loan_type || '',
          loan_term:               e.loan_term > 0 ? String(e.loan_term) : '',
          due_date:                e.due_date_full || '',
        });
        setEntryMethod('manual'); // Go to manual form with pre-filled data
      }
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to parse statement. Please try manual entry or a clearer image.');
      setEntryMethod(null);
    } finally {
      setUploading(false);
    }
  };

  // Handle type change
  const handleTypeChange = (newType) => {
    setForm({ ...EMPTY_FORM, type: newType, debt_subtype: newType });
    setErrors({});
  };

  // Handle field change
  const handleFieldChange = (key, value) => {
    setForm({ ...form, [key]: value });
    // Clear error for this field
    if (errors[key]) {
      setErrors({ ...errors, [key]: null });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const fields = FIELD_CONFIGS[form.type] || [];
    
    fields.forEach(field => {
      if (field.req && !form[field.key]) {
        newErrors[field.key] = 'Required';
      } else if (field.validate && form[field.key]) {
        const val = parseFloat(form[field.key]);
        if (!field.validate(val)) {
          if (field.key === 'balance') newErrors[field.key] = 'Must be > 0';
          else if (field.key === 'apr') newErrors[field.key] = 'Must be 0-100';
          else if (field.key === 'factor_rate') newErrors[field.key] = 'Must be 1.0-2.0';
          else if (field.key === 'holdback_percentage') newErrors[field.key] = 'Must be 1-100';
          else if (field.key.includes('remaining')) newErrors[field.key] = 'Must be > 0';
          else newErrors[field.key] = 'Invalid value';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Convert numeric strings to numbers
      const payload = { ...form };
      ['balance', 'original', 'apr', 'min_payment', 'credit_limit', 'loan_term', 'installments_remaining',
       'cash_price', 'total_lease_cost', 'early_buyout', 'factor_rate', 'holdback_percentage',
       'daily_sales', 'total_amount_owed', 'fee_amount', 'installment_agreement', 'late_fee'].forEach(key => {
        if (payload[key]) {
          payload[key] = parseFloat(payload[key]);
        } else {
          // Convert empty strings to 0 for numeric fields
          payload[key] = 0;
        }
      });

      // Set original to balance if not provided
      if (!payload.original) payload.original = payload.balance || 0;
      
      await api.createDebt(payload);
      
      // Handle completion - support both prop patterns
      if (onComplete) {
        onComplete();
      } else if (refreshData && navigate) {
        await refreshData();
        navigate(null, 'home');
      }
    } catch (err) {
      console.error('Error creating debt:', err);
      alert('Failed to add debt. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel - support both prop patterns
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (navigate) {
      navigate(null, 'home');
    }
  };

  const fields = FIELD_CONFIGS[form.type] || [];

  // Entry Method Selection Screen
  if (!entryMethod) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', background: 'var(--base)', padding: '20px 16px 80px' }}>
        <Horizon intensity={0.4} />
        
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 480, margin: '0 auto' }}>
          <div style={{ marginBottom: 32 }}>
            <Dp style={{ fontSize: 28, fontWeight: 900, color: 'var(--light)', display: 'block', marginBottom: 8 }}>
              Add Debt Account
            </Dp>
            <Mo style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.6 }}>
              Choose how you'd like to add your account
            </Mo>
          </div>

          {/* Option 1: Plaid Bank Connection */}
          <div 
            onClick={() => setEntryMethod('plaid')}
            style={{
              padding: 20,
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 16,
              marginBottom: 12,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(232, 168, 73, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                background: `linear-gradient(135deg, ${GOLD}, ${EMBER})`,
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {Ic.bank(24, BASE)}
              </div>
              <div style={{ flex: 1 }}>
                <Bd style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 4 }}>
                  Connect Your Bank
                </Bd>
                <Mo style={{ fontSize: 11, color: GOLD, fontWeight: 600, letterSpacing: 0.5 }}>
                  RECOMMENDED • FASTEST
                </Mo>
              </div>
            </div>
            <Mo style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.6 }}>
              Auto-import all your credit cards, loans, and accounts. Powered by Plaid • 11,000+ banks • Bank-grade encryption
            </Mo>
          </div>

          {/* Option 2: Upload Statement */}
          <label style={{ display: 'block', marginBottom: 12, cursor: 'pointer' }}>
            <input 
              type="file" 
              accept="image/*,application/pdf" 
              capture="environment"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={uploading}
            />
            <div 
              style={{
                padding: 20,
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(15px)',
                WebkitBackdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 16,
                transition: 'all 0.2s ease',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                opacity: uploading ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!uploading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 255, 204, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                <div style={{
                  width: 48,
                  height: 48,
                  background: 'linear-gradient(135deg, #00FFCC, #14B8A6)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {Ic.wand(24, BASE)}
                </div>
                <div style={{ flex: 1 }}>
                  <Bd style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 4 }}>
                    {uploading ? 'Processing...' : 'Upload Statement'}
                  </Bd>
                  <Mo style={{ fontSize: 11, color: '#00FFCC', fontWeight: 600, letterSpacing: 0.5 }}>
                    {uploading ? 'EXTRACTING DATA...' : 'SMART • AI-POWERED'}
                  </Mo>
                </div>
              </div>
              <Mo style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.6 }}>
                Take a photo or upload a PDF/image of your statement. AI extracts all the details automatically.
              </Mo>
            </div>
          </label>

          {/* Option 3: Manual Entry */}
          <div 
            onClick={() => setEntryMethod('manual')}
            style={{
              padding: 20,
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 16,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {Ic.edit(24, LIGHT)}
              </div>
              <div style={{ flex: 1 }}>
                <Bd style={{ fontSize: 16, fontWeight: 700, color: 'var(--light)', display: 'block', marginBottom: 4 }}>
                  Enter Manually
                </Bd>
                <Mo style={{ fontSize: 11, color: 'var(--mute)', fontWeight: 600, letterSpacing: 0.5 }}>
                  TRADITIONAL • PRECISE
                </Mo>
              </div>
            </div>
            <Mo style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.6 }}>
              Fill out the details yourself. Supports 11 debt types with custom fields for each.
            </Mo>
          </div>

          {/* Back Button */}
          <Btn secondary onClick={handleCancel} style={{ marginTop: 24, width: '100%' }}>
            CANCEL
          </Btn>
        </div>
      </div>
    );
  }

  // Plaid Loading State
  if (entryMethod === 'plaid') {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', background: 'var(--base)', padding: '20px 16px' }}>
        <Horizon intensity={0.4} />
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 480, margin: '0 auto', textAlign: 'center', paddingTop: 100 }}>
          <div className="pulse-a" style={{
            width: 80,
            height: 80,
            background: `linear-gradient(135deg, ${GOLD}, ${EMBER})`,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            {Ic.bank(40, BASE)}
          </div>
          <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', marginBottom: 12 }}>
            {plaidSyncing ? 'Importing Accounts...' : 'Opening Plaid...'}
          </Dp>
          <Mo style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.6, marginBottom: 32 }}>
            {plaidSyncing ? 'Please wait while we sync your accounts' : 'Connecting to your bank securely'}
          </Mo>
          <Btn secondary onClick={() => setEntryMethod(null)}>
            CANCEL
          </Btn>
        </div>
      </div>
    );
  }

  // Manual Entry Form (existing dynamic forms)
  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: 'var(--base)', padding: '20px 16px 80px' }}>
      <Horizon intensity={0.4} />
      
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 480, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <button 
            onClick={() => setEntryMethod(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: GOLD,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '8px 0',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            ← BACK TO OPTIONS
          </button>
          <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block', marginBottom: 6 }}>
            Add Debt Account
          </Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', lineHeight: 1.6 }}>
            Select your debt type and enter the details
          </Bd>
        </div>

        {/* Type Selector */}
        <TypeSelectorGrid selectedType={form.type} onSelect={handleTypeChange} />

        {/* MCA Tooltip */}
        {form.type === 'mca' && <McaTooltip />}

        {/* Dynamic Fields */}
        <div style={{
          opacity: 1,
          animation: 'fadeIn 0.3s ease-in',
          marginBottom: 24
        }}>
          {fields.map(field => (
            <DynamicField
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={handleFieldChange}
              errors={errors}
            />
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Btn secondary onClick={handleCancel} disabled={isSubmitting} data-testid="cancel-btn">
            CANCEL
          </Btn>
          <Btn gold onClick={handleSubmit} disabled={isSubmitting} data-testid="save-debt-btn">
            {isSubmitting ? 'SAVING...' : 'ADD DEBT'}
          </Btn>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
